import React, { useState, useEffect, useContext } from 'react'
import { node, func, object } from 'prop-types'
import { navigate } from 'gatsby'
import omit from 'lodash/omit'
import moment from 'moment'
import queryString from 'query-string'
import { format, differenceInYears, parseISO } from 'date-fns'

import { stripPrefix } from 'utils/routing'

import { trackEvent } from 'utils/tracking'
import { PROTECTED_ROUTES } from 'utils/constants'
import * as NativeAuth from '../lib/auth-sdk'
import { checkoutUpdateCustomAttribute } from 'queries/checkout'
import useDigitalData from 'hooks/useDigitalData'
import CheckoutContext from 'contexts/Checkout'

const NativeAuthContext = React.createContext()

const NativeAuthProvider = ({ children, checkout, setAuthSdk, setCheckoutUser }) => {
  const { removeSubscriptionItemsFromCheckout, setDraftOrder } = useContext(CheckoutContext)
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(process.browser && localStorage.user ? JSON.parse(localStorage.user) : null)
  const [province, setProvince] = useState(process.browser && localStorage.province ? localStorage.province : null)
  const [token, setToken] = useState(process.browser && localStorage.token ? localStorage.token : null)
  const [signupError, setSignupError] = useState('')
  const [loginError, setLoginError] = useState('')
  const [signUpModalVisibility, setSignupModalVisibility] = useState(false)
  const [loginModalVisibility, setLoginModalVisibility] = useState(false)
  const [missingAddressModalVisibility, setMissingAddressModalVisibility] = useState(false)
  const [cancelSubscriptionModalVisibility, setCancelSubscriptionModalVisibility] = useState(false)
  const [pauseSubscriptionModalVisibility, setPauseSubscriptionModalVisibility] = useState(false)
  const [subscriptionModalVisible, setSubscriptionModalVisible] = useState(false)
  const [verifyEmailModalVisibility, setVerifyEmailModalVisibility] = useState(false)
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false)
  const [vypePlusDiscountCodesModalVisibility, setVypePlusDiscountCodesModalVisibility] = useState(false)
  const [participatesTo2020Lottery, setParticipatesTo2020Lottery] = useState(false)
  const [isBoostPlanModalVisible, setIsBoostPlanModalVisible] = useState(false)
  const [userEmailAutofillValue, setUserEmailAutofillValue] = useState(null)
  const [christmasPopupVisible, setChristmasPopupVisible] = useState(false)
  const [softVerifiedUserState, setSoftVerifiedUserState] = useState(null)
  const [digitalDataPopulated, setDigitalDataPopulated] = useState(false)

  const { clearCheckout } = useContext(CheckoutContext)

  const { digitalData } = useDigitalData()

  useEffect(() => {
    const init = async () => {
      NativeAuth.init({
        storeName: 'vype',
        name: 'vype',
        website: 'govype.ca',
        phone: '1-844-746-8973',
        email: 'info@govype.ca',
        authApiUrl: process.env.GATSBY_AUTH_API,
        frontend: process.env.AUTH_FRONTEND
      })

      NativeAuth.iframe.addEventListener('load', initialAuthCheck)
      setAuthSdk(NativeAuth)
    }

    init()

    if (window.localStorage?.province === 'QC') {
      navigate('/qc')
    }
  }, [])

  useEffect(() => {
    if (participatesTo2020Lottery && user && token) {
      updateUser({ participatesTo2020Lottery })
      setParticipatesTo2020Lottery(false)
    }
  }, [user, token, participatesTo2020Lottery])

  useEffect(() => {
    setCheckoutUser(user)
  }, [user])

  useEffect(() => {
    if (!user) return

    try {
      digitalData.push({
        user: {
          userInfo: {
            age: moment().diff(moment(user.dob), 'years'),
            memberId: user.id,
            emailVerified: user.emailVerified,
            postalCode: user.postalCode.substring(0, 3),
            stateProvince: user.province
          },
          loyaltyInfo: {
            memberSince: user.createdAt,
            newsletterSubscribed: user.emailSubscriptionVype ? 'true' : 'false',
            lifetimeSpend: parseFloat(user.totalSpentAmount || '0.00').toFixed(2),
            status: user.currentTierLevel || 'non-vuse plus user'
          },
          subscriptionInfo: {
            program:
              user.subscription?.items
                .map(i => i.lineItemData.title + ',' + i.quantity + ',' + i.lineItemData.variant.title)
                .join('|') || null
          }
        }
      })
    } catch (e) {
      console.log(e)
    }
  }, [user])

  const initialAuthCheck = async () => {
    if (localStorage.softVerifiedUser) setLoading(false)
    const currentQueryString = queryString.parse(window.location.search)
    const { token: urlToken } = currentQueryString

    let [{ user, token }, { softVerifiedUser }] = await Promise.all([
      NativeAuth.getUserState(),
      NativeAuth.getSoftVerifiedUser()
    ])
    if (!token) {
      if (urlToken) {
        try {
          const userResponse = await NativeAuth.getUser(urlToken)
          user = userResponse.user
          localStorage.setItem('token', urlToken)
          if (urlToken) token = urlToken
        } catch (e) {
          console.log(e)
        }
      }
    }

    if (token && urlToken) {
      console.log('alredy logged in ? ', currentQueryString)
      currentQueryString.token = undefined
      const strippedQueryString = queryString.stringify(currentQueryString)
      console.log(`redirecting to`, `${stripPrefix(window.location.pathname)}?${strippedQueryString}`)
      navigate(`${stripPrefix(window.location.pathname)}?${strippedQueryString}`)
    }

    setUser(user)
    setToken(token)
    setProvince(user?.province || softVerifiedUser?.user?.province)

    // Also re-fetch the user from the server to update the SDK's cache
    if (token) {
      NativeAuth.getUser(token)
        .then(({ user, token }) => {
          setUser(user)
          localStorage.setItem('user', JSON.stringify(user))
          setToken(token)
          setProvince(user?.province || softVerifiedUser?.user?.province)

          // window.ire('identify', {
          //   customerId: user.id,
          //   customerEmail: sha1(user.email)
          // })

          return
        })
        .catch(() => {
          NativeAuth.logout()

          setUser(null)
          setToken(null)
          localStorage.removeItem('user')
          localStorage.removeItem('token')

          if (softVerifiedUser) {
            setProvince(softVerifiedUser.user.province)
            localStorage.setItem('province', softVerifiedUser.user.province)
          } else {
            setProvince(null)
            localStorage.removeItem('province')
          }
        })
    }

    if (user) {
      localStorage.setItem('user', JSON.stringify(user))
      localStorage.setItem('province', user.province)
      localStorage.setItem('token', token)
      setSoftVerifiedUserState({ user: { province: user.province } })

      window.dataObject.user = {
        userID: user.id,
        verifiedEmail: user.emailVerified
      }
    } else if (softVerifiedUser && softVerifiedUser.user) {
      setSoftVerifiedUserState(softVerifiedUser)
      localStorage.setItem('softVerifiedUser', JSON.stringify(softVerifiedUser.user))
      localStorage.setItem('province', softVerifiedUser.user.province)
      localStorage.removeItem('user')
      localStorage.removeItem('token')
      // window.ire('identify', { customerId: '', customerEmail: '' })
    } else {
      localStorage.removeItem('user')
      localStorage.removeItem('token')
      localStorage.removeItem('province')
      localStorage.removeItem('softVerifiedUser')

      // window.ire('identify', { customerId: '', customerEmail: '' })
    }
    setLoading(false)
  }

  const login = async ({ email, password }) => {
    try {
      const response = await NativeAuth.login(email, password)
      setUser(response.user)
      setToken(response.token)
      setCheckoutJwtToken(response.token)
      setProvince(response.user.province)
      localStorage.setItem('token', response.token)
      localStorage.setItem('user', JSON.stringify(response.user))
      localStorage.setItem('province', response.user.province)
      setLoginError('')
      setLoginModalVisibility(false)
      setParticipatesTo2020Lottery(false)

      trackEvent({
        name: 'loginTrue',
        time: Date()
      })

      window.dataObject.user = {
        userID: response.user.id,
        verifiedEmail: response.user.emailVerified
      }

      setUserEmailAutofillValue(null)

      try {
        digitalData.push({
          user: {
            userInfo: {
              age: differenceInYears(new Date(), parseISO(response.user.dob)),
              memberId: response.user.id,
              postalCode: response.user.postalCode.substr(0, 3) || '',
              stateProvince: response.user.province || ''
            },
            loyaltyInfo: {
              memberSince: format(parseISO(response.user.createdAt), 'M/d/yyyy'),
              newsletterSubscribed: response.user.emailSubscriptionVype,
              lifetimeSpend: response.user.totalSpentAmount,
              status: response.user.lastTierLevel
            }
          }
        })

        window._satellite.track('login-complete')
      } catch (e) {
        console.log(e)
      }
    } catch (e) {
      if (e?.status === 401 || e?.response?.status === 401) {
        setLoginError('Email or password incorrect')
      } else {
        setLoginError('An unknown error has occured. Please try again later.')
      }
    }
  }

  const signup = async user => {
    if (!user.email.match(/@(volume7|bat|deloitte|deloittece|yopmail)\.(io|ca|com|co\.uk)$/)) {
      const response = await NativeAuth.screenEmail(user.email)

      if (response.error === 'Email account invalid') {
        setSignupError('emailAccountInvalid')
        return 'emailAccountInvalid'
      }
    }

    try {
      const response = await NativeAuth.signup({
        user: {
          ...omit(user, ['passwordConfirm', 'addressLabel', 'street', 'manualEntry', 'isManualEntry']),
          address: user.street,
          participatesTo2020Lottery,
          referralCode: user.referralCode,
          thirdPartyBoostSource: user.thirdPartyBoostSource
        },
        storeName: 'vype'
      })

      if (response.method === 'idVerification') {
        setSignupError('idVerification')
        return 'idVerification'
      } else {
        setUser(response.user)
        setToken(response.token)
        setCheckoutJwtToken(response.token)
        setProvince(response.user.province)
        localStorage.setItem('token', response.token)
        localStorage.setItem('user', JSON.stringify(response.user))
        localStorage.setItem('province', JSON.stringify(response.user.province))
        setSignupError('')

        try {
          window.dataLayer.push({
            event: 'formSubmitHardAgeValidation',
            eventCategory: 'Activate',
            eventAction: 'Soft conversion',
            eventLabel: 'Hard age validation',
            pagePath: '/epod',
            language: 'En',
            province: user.province,
            connexion: 'yes'
          })

          digitalData.push({
            user: {
              userInfo: {
                age: differenceInYears(new Date(), parseISO(response.user.dob)),
                memberId: response.user.id,
                postalCode: response.user.postalCode.substr(0, 3) || '',
                stateProvince: response.user.province || ''
              },
              loyaltyInfo: {
                memberSince: format(parseISO(response.user.createdAt), 'M/d/yyyy'),
                newsletterSubscribed: response.user.emailSubscriptionVype
              }
            },
            page: {
              virtualUrl: null
            }
          })

          window._satellite.track('registration-complete')
        } catch (e) {
          console.log(e)
        }
      }

      setUserEmailAutofillValue(null)
    } catch (e) {
      console.log(e)
      if (e.data === 'emailAlreadyExists' || e.data.message === 'emailAlreadyExists') {
        setSignupError('emailAlreadyExists')
        return 'emailAlreadyExists'
      } else if (e.data === 'accountInvalid' || e.data.message === 'accountInvalid') {
        setSignupError('accountInvalid')
        return 'accountInvalid'
      } else {
        return e.data
      }
    }
  }

  const setNativeSoftVerifiedUser = async (user, redirectAfter = true) => {
    try {
      NativeAuth.setSoftVerifyUser({ user })
      localStorage.setItem('softVerifiedUser', JSON.stringify(user))
      localStorage.setItem('province', user.province)
      setProvince(user.province)
      setSoftVerifiedUserState({ user })

      if (redirectAfter) {
        if (user.province === 'QC') {
          navigate('/qc/')
        }
      }
    } catch (e) {
      console.log(e)
    }
  }

  const logout = () => {
    NativeAuth.logout()
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    setUser(null)
    setToken(null)
    clearCheckout()
    if (localStorage.softVerifiedUser) {
      setProvince(JSON.parse(localStorage.softVerifiedUser).province)
    }
    if (PROTECTED_ROUTES.includes(location.pathname)) {
      navigate('/')
    }
    delete window.dataObject.user
    window.dataLayer?.push({ user: null })
    // window.ire('identify', { customerId: '', customerEmail: '' })
  }

  const updateUser = async body => {
    setLoading(true)
    const user = await NativeAuth.updateUser(body, token)
    localStorage.setItem('user', JSON.stringify(user))
    localStorage.setItem('province', JSON.stringify(user.province))
    setUser(user)
    setProvince(user.province)
    setLoading(false)
    return user
  }

  const refreshUserData = async () => {
    if (!token) return
    const { user } = await NativeAuth.getUser(token)
    localStorage.setItem('user', JSON.stringify(user))
    setUser(user)
  }

  const forgotPassword = async body => {
    await NativeAuth.forgotPassword(body)
    setForgotPasswordSuccess(true)
  }

  const resetPassword = NativeAuth.resetPassword

  const setCheckoutJwtToken = async token => {
    await checkoutUpdateCustomAttribute({
      checkoutId: checkout.id,
      input: {
        customAttributes: [
          {
            key: 'jwt',
            value: token
          }
        ]
      }
    })
  }

  const verifyEmail = async body => {
    await NativeAuth.verifyEmail(body)

    if (user) {
      setUser({ ...user, emailVerified: true })
      localStorage.setItem('user', JSON.stringify({ ...user, emailVerified: true }))
      NativeAuth.updateCache({ user: { ...user, emailVerified: true } })
    }
  }

  const addPaymentMethod = async body => {
    await NativeAuth.addPaymentMethod(body)
    const { user } = await NativeAuth.getUser(token)
    setUser(user)
  }

  const deletePaymentMethod = async body => {
    await NativeAuth.deletePaymentMethod(body)
    const { user } = await NativeAuth.getUser(token)
    setUser(user)
  }

  const editSubscription = async body => {
    setLoading(true)
    await NativeAuth.editSubscription(omit(body, ['silent']))
    const { user } = await NativeAuth.getUser(token)
    setUser(user)

    if (!body.silent) {
      navigate('/account/subscriptions/?subscriptionStatus=updated')
    }

    setLoading(false)
  }

  const cancelSubscription = async body => {
    await NativeAuth.cancelSubscription(body)
    const { user } = await NativeAuth.getUser(token)
    setUser(user)
  }

  const cancelOrder = async body => {
    setLoading(true)
    await NativeAuth.cancelOrder(body)
    const { user } = await NativeAuth.getUser(token)
    setUser(user)
    setLoading(false)
  }

  const getOrder = async orderId => {
    const { order } = await NativeAuth.getOrder({ orderId, token })
    return order
  }

  const submitCalloutApplication = async data => {
    await NativeAuth.submitCalloutApplication({ data, token })
  }

  const createBoostPlanOrder = async data => {
    const { order } = await NativeAuth.createBoostPlanOrder({ data, token })
    return order
  }

  const submitCalloutVotes = async data => {
    const { callout_vote_options: calloutVoteOptions } = data
    await NativeAuth.submitCalloutVotes(data, token)
    setUser({
      ...user,
      calloutVoteOptions
    })
  }

  const claimTransitionEpod = data => NativeAuth.claimTransitionEpod(data, token)

  // const claimTransitionEpod = async data => {
  //   const { order } = await NativeAuth.claimTransitionEpod(data, token)
  //   return order
  // }

  const editSubscriptionWithDraftOrder = async ({ items, redirect = true }) => {
    await editSubscription({
      id: user?.subscription.id,
      token,
      items: items.map(item => ({
        subscriptionId: user?.subscription.id,
        variantId: item.lineItemData.variant.id,
        quantity: item.quantity,
        lineItemData: item.lineItemData
      }))
    })

    await removeSubscriptionItemsFromCheckout()

    setDraftOrder({})

    if (redirect) {
      navigate('/account/subscriptions/?subscriptionStatus=updated')
    }
  }

  const validateEmail = NativeAuth.validateEmail

  const value = {
    loading,
    user,
    setUser,
    token,
    province,
    login,
    signup,
    updateUser,
    logout,
    loginError,
    signupError,
    setSignupError,
    screenEmail: NativeAuth?.screenEmail,
    setNativeSoftVerifiedUser,
    softVerifiedUserState,
    signUpModalVisibility,
    setSignupModalVisibility,
    loginModalVisibility,
    setLoginModalVisibility,
    missingAddressModalVisibility,
    setMissingAddressModalVisibility,
    verifyEmailModalVisibility,
    setVerifyEmailModalVisibility,
    verifyEmail,
    sendVypeVerificationEmail: NativeAuth?.sendVypeVerificationEmail,
    forgotPassword,
    forgotPasswordSuccess,
    resetPassword,
    editSubscription,
    cancelSubscription,
    cancelSubscriptionModalVisibility,
    setCancelSubscriptionModalVisibility,
    pauseSubscriptionModalVisibility,
    setPauseSubscriptionModalVisibility,
    vypePlusDiscountCodesModalVisibility,
    setVypePlusDiscountCodesModalVisibility,
    participatesTo2020Lottery,
    setParticipatesTo2020Lottery,
    cancelOrder,
    deletePaymentMethod,
    addPaymentMethod,
    authSdk: NativeAuth,
    getOrder,
    submitCalloutApplication,
    setIsBoostPlanModalVisible,
    isBoostPlanModalVisible,
    createBoostPlanOrder,
    validateEmail,
    setProvince,
    claimTransitionEpod,
    submitCalloutVotes,
    subscriptionModalVisible,
    setSubscriptionModalVisible,
    editSubscriptionWithDraftOrder,
    userEmailAutofillValue,
    setUserEmailAutofillValue,
    digitalDataPopulated,
    setDigitalDataPopulated,
    refreshUserData,
    christmasPopupVisible,
    setChristmasPopupVisible
  }

  return <NativeAuthContext.Provider value={value}>{children}</NativeAuthContext.Provider>
}

NativeAuthProvider.propTypes = {
  children: node.isRequired,
  checkout: object,
  setAuthSdk: func,
  setCheckoutUser: func
}

const { Consumer } = NativeAuthContext

export { Consumer as NativeAuthConsumer, NativeAuthProvider }

export default NativeAuthContext
